import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RGL, { WidthProvider } from 'react-grid-layout';
import { SystemText } from '@salesperformanceint/e4shared';

import PageHeader from 'components/general/pageHeader';
import TeamList from './components/teamList';
import LatestBadges from './components/latestBadges';
import Leaderboard from './components/leaderboard';
import CRMGoals from '../user/1-2-1s/components/crmGoals';
import LatestBlogPosts from './components/blogPosts';
import LatestQuestions from './components/latestQuestions';
import WhatsHappening from './components/whatsHappening';
import WhatsHappeningModal from './components/whatsHappening/components/whatsHappeningModal';
import Badges from 'components/user/badges';

import { setHomeWhatsHappening } from 'actions/dashboard';
import { setUserPreferences } from 'actions/user';
import { hasModule } from 'helpers/profile';
import { apiPatch } from 'services';

import './home.css';

const ReactGridLayout = WidthProvider(RGL);

export const AvailableHomeWidgets = [
    'badges',
    'blogs',
    'goals',
    'knowledge',
    'leaderboard',
    'team',
    'coach',
    'whatsHappening',
];

export const Home = ({ history, testWidth }) => {
    const dispatch = useDispatch();

    const defaultProps = {
        className: 'layout',
        items: 20,
        rowHeight: 100,
        cols: 6,
        height: item => 110 * item.h - 50,
    };

    const text = {
        welcome: SystemText('Home/Welcome'),
        widgets: {
            whatsHappenning: SystemText('Home/Widgets/WhatsHappening/Title'),
            team: SystemText('Home/Widgets/Team/Title'),
            coach: SystemText('Home/Widgets/Coach/Title'),
            badges: SystemText('Home/Widgets/Badges/Title'),
            goals: SystemText('Home/Widgets/Goals/Title'),
            posts: SystemText('Home/Widgets/Posts/Title'),
            knowledge: SystemText('Home/Widgets/Knowledge/Title'),
            leaderboard: SystemText('Home/Widgets/Leaderboard/Title'),
        },
        leaderboardAll: SystemText('Home/Widgets/Leaderboard/All'),
        leaderboardYTD: SystemText('Home/Widgets/Leaderboard/YTD'),
        leaderboardQTD: SystemText('Home/Widgets/Leaderboard/QTD'),
        leaderboardMTD: SystemText('Home/Widgets/Leaderboard/MTD'),
    };

    const container = React.useRef(null);
    const min = 1000;
    const user = useSelector(state => state.user.user);
    const homeWidgets = user?.companySettings?.display?.homeWidgets || AvailableHomeWidgets;
    const leftAsideOpen = useSelector(state => state.ui.leftAsideOpen);
    const [divWidth, setDivWidth] = React.useState(testWidth || 0);
    const [prefs, setPrefs] = React.useState(user?.preferences);
    const [editing, setEditing] = React.useState(false);
    const [leaderboardRange, setLeaderboardRange] = React.useState('month');
    const [showBadges, setShowBadges] = React.useState(false);
    const [lastPaint, setLastPaint] = React.useState(null);

    React.useEffect(() => {
        if (user) {
            const { prism_UI } = user.companySettings;

            if (prism_UI) {
                history.replace('/welcome');
            } else {
                const { hideHomePage } = user.companySettings.display;

                if (hideHomePage) {
                    if (user.isManager || user.administrator) {
                        history.replace('/insights');
                    } else {
                        history.replace('/develop/performance-dashboard');
                    }
                }
            }
        }
    }, [user]);

    const resizeHandler = () => {
        setTimeout(() => {
            if (container.current) {
                setDivWidth(container.current.offsetWidth);
            }
        }, 100);
    };

    React.useEffect(() => {
        container.current && setDivWidth(container.current.offsetWidth);
    }, [container.current]);

    React.useEffect(() => {
        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
            dispatch(setHomeWhatsHappening({}));
        };
    }, []);

    React.useEffect(() => {
        setTimeout(() => window.dispatchEvent(new Event('resize')), 250);
    }, [leftAsideOpen, lastPaint]);

    const widgetsMapped = React.useCallback(() => {
        if (divWidth === 0) return null;

        let all_y = 0;

        const preferences = JSON.parse(prefs);
        const hasGoals = user.hasGoals;
        const widgets = [
            ...defaultWidgets.filter(
                w => (user.isManager || !w.manager) && (user.coaching_Ids?.length > 0 || !w.coach) && (hasGoals || !w.goals)
            ),
        ];

        if (preferences?.home) {
            widgets.forEach(w => {
                const myW = preferences.home.find(h => h.ref === w.ref) || {};
                const thisOrThat = x => (w[x] = isNaN(myW[x]) ? w[x] : myW[x]);
                thisOrThat('x');
                thisOrThat('y');
                thisOrThat('h');
                thisOrThat('w');
            });
        }

        return widgets.map((item, i) => {
            const w = divWidth < min ? 6 : item.w;
            const x = divWidth < min ? 0 : item.x;
            const y = divWidth < min ? all_y : item.y;
            const h = item.h;
            all_y += item.h;

            if (item.module && !hasModule(user, item.module)) return null;
            if (item.coach && !user.companySettings?.development?.allowCoaches) return null;

            if (
                homeWidgets &&
                !homeWidgets.map(wmap => (wmap === 'knowledge' ? 'kb' : wmap)).some(wsome => wsome === item.ref)
            )
                return null;

            const newProps = { x, y, h, w, range: leaderboardRange };

            const control = React.createElement(item.control, {
                id: `home-${item.ref}`,
                item: { ...item, ...newProps },
                profile: user,
                setLastPaint,
                history,
                home: true,
                ...item.props,
            });

            return (
                <div
                    className="grid-item"
                    key={`${item.ref}`}
                    data-grid={{ x, y, w, h, minH: item.minH || 2, maxH: item.maxH, minW: 2 }}
                >
                    <span>
                        <div id={`home-${item.ref}`} style={{ height: '100%' }}>
                            {control}
                        </div>
                    </span>
                </div>
            );
        });
    }, [prefs, divWidth, leaderboardRange]);

    const onLayoutChange = ({ ...props }) => {
        if (divWidth >= min && editing) {
            const home = Object.values(props).map(v => ({
                ref: v.i,
                h: v.h,
                w: v.w,
                x: v.x,
                y: v.y,
            }));

            const preferences = { ...JSON.parse(user.preferences), home };
            const json = JSON.stringify(preferences);
            apiPatch('preferences', { preferences: json }, user.guid, () => {
                setPrefs(json);
                dispatch(setUserPreferences(json));
            });
        }
    };

    const LeaderboardButtons = () => {
        const button = (name, value) => {
            return (
                <button
                    key={value}
                    onClick={() => setLeaderboardRange(value)}
                    className={`btn btn-xs ${leaderboardRange === value ? 'btn-primary' : ''}`}
                >
                    {name}
                </button>
            );
        };

        return (
            <div className="pull-right btn-row" onMouseDown={e => e.stopPropagation()}>
                {button(text.leaderboardAll, '')}
                {button(text.leaderboardYTD, 'year')}
                {button(text.leaderboardQTD, 'quarter')}
                {button(text.leaderboardMTD, 'month')}
            </div>
        );
    };

    const defaultWidgets = [
        { ref: 'whatsHappening', name: text.widgets.whatsHappenning, x: 0, y: 0, w: 2, h: 4, control: WhatsHappening },
        { ref: 'team', name: text.widgets.team, x: 2, y: 0, w: 2, h: 4, control: TeamList, manager: true },
        { ref: 'badges', name: text.widgets.badges, x: 4, y: 0, w: 2, h: 4, control: LatestBadges, setShowBadges },
        {
            ref: 'goals',
            module: 'integrations',
            name: text.widgets.goals,
            x: 2,
            y: 4,
            w: 2,
            h: 4,
            minH: 3,
            control: CRMGoals,
            home: true,
            goals: true,
        },
        {
            ref: 'leaderboard',
            name: text.widgets.leaderboard,
            x: 4,
            y: 4,
            w: 2,
            h: 4,
            control: Leaderboard,
            extra: LeaderboardButtons,
        },
        { ref: 'blogs', module: 'blogs', name: text.widgets.posts, x: 0, y: 8, w: 2, h: 4, control: LatestBlogPosts },
        {
            ref: 'kb',
            module: 'knowledge',
            name: text.widgets.knowledge,
            x: 2,
            y: 8,
            w: 2,
            h: 4,
            control: LatestQuestions,
        },
        {
            ref: 'coach',
            name: text.widgets.coach,
            x: 2,
            y: 8,
            w: 2,
            h: 4,
            control: TeamList,
            coach: true,
            props: { coach: true },
        },
    ];

    return (
        <div id="homepage" ref={container}>
            <div>
                <PageHeader title={SystemText('Home/Welcome')} />
                <section className="content home mtm10" data-testid="home-content">
                    <ReactGridLayout
                        key={`reload-${divWidth}`}
                        {...defaultProps}
                        isDraggable={Boolean(divWidth >= min)}
                        draggableCancel="a,button,.pointer,input"
                        onBreakpointChange={null}
                        onLayoutChange={onLayoutChange}
                        onDrop={() => setEditing(true)}
                        onResizeStart={() => setEditing(true)}
                        onDragStart={() => setEditing(true)}
                        measureBeforeMount={false}
                        useCSSTransforms={false}
                        compactType="vertical"
                        preventCollision={false}
                    >
                        {widgetsMapped()}
                    </ReactGridLayout>
                </section>
                <WhatsHappeningModal />
                <Badges show={showBadges} close={() => setShowBadges(false)} profileId={showBadges} />
            </div>
        </div>
    );
};

export default Home;
